import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { RegistryCategory, ExplorerItem, ViewMode } from './types';
import { CompactRow } from './CompactRow';
import { Card } from '../../../../../components/ui/Card';
interface CategorySectionProps {
  category: RegistryCategory;
  items: ExplorerItem[];
  viewMode: ViewMode;
  onSelectItem: (id: string) => void;
  isExpanded: boolean;
  onToggleExpanded: () => void;
  accentColor?: 'blue' | 'purple';
  renderCardContent?: (item: ExplorerItem) => React.ReactNode;
}
export function CategorySection({
  category,
  items,
  viewMode,
  onSelectItem,
  isExpanded,
  onToggleExpanded,
  accentColor = 'blue',
  renderCardContent
}: CategorySectionProps) {
  const Icon = category.icon;
  if (items.length === 0) return null;
  const activeCount = items.filter(i => i.usageCount > 0).length;
  const accentClass = accentColor === 'blue' ? 'text-blue-600' : 'text-purple-600';
  const bgAccentClass = accentColor === 'blue' ? 'bg-blue-50' : 'bg-purple-50';
  return <div className="mb-4 sm:mb-6 bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-6082">
      <button onClick={onToggleExpanded} className="w-full px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between bg-gray-50/50 hover:bg-gray-50 transition-colors border-b border-gray-100 gap-3" data-id="element-6083">
        <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 flex-1" data-id="element-6084">
          <div className={`w-7 h-7 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center shrink-0 ${bgAccentClass} ${accentClass}`} data-id="element-6085">
            <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" data-id="element-6086" />
          </div>
          <div className="text-left min-w-0" data-id="element-6087">
            <h4 className="font-bold text-sm sm:text-base text-gray-900 truncate" data-id="element-6088">
              {category.label}
            </h4>
            <p className="text-[11px] sm:text-xs text-gray-500 truncate" data-id="element-6089">
              <span className="sm:hidden" data-id="element-6090">
                {activeCount}/{items.length} actief
              </span>
              <span className="hidden sm:inline" data-id="element-6091">{category.description}</span>
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0" data-id="element-6092">
          <div className="text-right hidden sm:block" data-id="element-6093">
            <div className="text-sm font-medium text-gray-900" data-id="element-6094">
              {activeCount} / {items.length} actief
            </div>
          </div>
          {isExpanded ? <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" data-id="element-6095" /> : <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" data-id="element-6096" />}
        </div>
      </button>

      <AnimatePresence initial={false} data-id="element-6097">
        {isExpanded && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="overflow-hidden" data-id="element-6098">
            <div className="p-3 sm:p-5" data-id="element-6099">
              {viewMode === 'compact' ? <div className="flex flex-col gap-1.5 sm:gap-2" data-id="element-6100">
                  {items.map(item => <CompactRow key={item.id} item={item} onClick={() => onSelectItem(item.id)} accentColor={accentColor} data-id="element-6101" />)}
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-id="element-6102">
                  {items.map(item => {
              const ItemIcon = item.icon;
              const isActive = item.usageCount > 0;
              const hoverClass = accentColor === 'blue' ? 'hover:border-blue-300' : 'hover:border-purple-300';
              return <Card key={item.id} className={`p-3 sm:p-4 h-full flex flex-col transition-all duration-200 ${isActive ? `cursor-pointer group relative ${hoverClass} border-gray-200 hover:shadow-md` : 'opacity-50 border-gray-100 bg-gray-50/50 cursor-not-allowed'}`} onClick={() => isActive && onSelectItem(item.id)} data-id="element-6103">
                        <div className="flex items-start justify-between mb-2.5 sm:mb-3" data-id="element-6104">
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center ${isActive ? `${bgAccentClass} ${accentClass} group-hover:scale-105 transition-transform` : 'bg-gray-200 text-gray-400'}`} data-id="element-6105">
                            <ItemIcon className="w-4 h-4 sm:w-5 sm:h-5" data-id="element-6106" />
                          </div>
                          <div className="text-right" data-id="element-6107">
                            <div className={`text-base sm:text-lg font-black ${isActive ? accentClass : 'text-gray-400'}`} data-id="element-6108">
                              {item.usageCount}
                            </div>
                            <div className="text-[10px] text-gray-500 font-medium" data-id="element-6109">
                              refs
                            </div>
                          </div>
                        </div>

                        <div className="mb-2.5 sm:mb-3 min-w-0" data-id="element-6110">
                          <div className="flex items-center gap-2 mb-1 min-w-0" data-id="element-6111">
                            <code className="text-xs sm:text-sm font-mono font-bold text-gray-900 truncate" data-id="element-6112">
                              {item.id}
                            </code>
                            {!item.isRegistered && <span className="text-[8px] bg-red-100 text-red-700 px-1 py-0.5 rounded font-bold uppercase tracking-wider shrink-0" data-id="element-6113">
                                Unreg
                              </span>}
                          </div>
                          <p className="text-[11px] sm:text-xs text-gray-500 line-clamp-2" title={item.description} data-id="element-6114">
                            {item.description}
                          </p>
                        </div>

                        {renderCardContent && isActive && <div className="mt-auto pt-2.5 sm:pt-3 border-t border-gray-100" data-id="element-6115">
                            {renderCardContent(item)}
                          </div>}

                        {!renderCardContent && isActive && <div className="mt-auto pt-2.5 sm:pt-3 border-t border-gray-100" data-id="element-6116">
                            <div className="flex flex-wrap gap-1" data-id="element-6117">
                              {Array.from(item.modules).sort().slice(0, 3).map(m => <span key={m} className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-gray-50 border-gray-200 text-gray-600" data-id="element-6118">
                                    {m}
                                  </span>)}
                              {item.modules.size > 3 && <span className="text-[10px] text-gray-400 font-medium px-1 py-0.5" data-id="element-6119">
                                  +{item.modules.size - 3}
                                </span>}
                            </div>
                          </div>}
                      </Card>;
            })}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}